import React from 'react';
import { useSessionTimeout, SESSION_TIMEOUT_MINUTES } from '@/hooks/useSessionTimeout';
import { useAuthStore } from '@/stores/authStore';

/**
 * HIPAA Session Timeout Warning Component
 *
 * Displays a warning overlay when the user's session is about to expire
 * due to inactivity. Any mouse/keyboard activity automatically dismisses
 * the warning and resets the timer. If no activity, user is logged out.
 */
export const SessionTimeoutWarning: React.FC = () => {
  const { isPrompted, remainingTime } = useSessionTimeout();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Don't show if not authenticated or not prompted
  if (!isAuthenticated || !isPrompted) return null;

  const remainingSeconds = Math.ceil(remainingTime / 1000);
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden border border-gray-200">
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <i className="fas fa-clock text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">
                Session Timeout
              </h2>
              <p className="text-amber-100 text-xs">
                HIPAA Security
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          {/* Countdown - prominent */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center bg-gray-900 rounded-lg px-5 py-2">
              <span className="text-2xl font-mono font-bold text-white tracking-wider">
                {minutes}:{seconds.toString().padStart(2, '0')}
              </span>
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center">
            You will be logged out due to inactivity.
          </p>

          {/* Activity hint */}
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
            <i className="fas fa-mouse-pointer"></i>
            <span>Move mouse or press any key to continue</span>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-2.5 border-t border-gray-100">
          <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1">
            <i className="fas fa-shield-alt"></i>
            {SESSION_TIMEOUT_MINUTES}-minute inactivity limit protects patient data
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
